import React from 'react';
import { 
  ScrollText, Key, BookOpen, Database, UserCheck, AlertTriangle, 
  RefreshCw, Ban, FileSignature, Edit3, Scale, Calendar,
  Shield, Users, AlertCircle, CheckCircle
} from 'lucide-react';

const CGU = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br w-full from-slate-50 to-gray-100 2xl:text-lg xl:text-base">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* En-tête */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-gray-800 rounded-2xl">
              <ScrollText className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Conditions Générales d'Utilisation (CGU)
          </h1>
          <p className="text-xl text-gray-600">TANOED</p>
          <div className="w-24 h-1 bg-gray-800 mx-auto rounded-full mt-4"></div>
        </div>

        {/* Conditions d'accès */}
        <section className="bg-white rounded-2xl flex flex-col shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Key className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Conditions d'accès et modalités d'inscription
            </h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed">
            Les modalités d'accès au service TANOED sont entièrement déléguées au 
            <span className="font-semibold text-gray-900"> Gestionnaire d'Accès aux Ressources (GAR)</span>.
          </p>
        </section>

        {/* Caractère obligatoire et utilisation */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-green-100 rounded-xl">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Caractère obligatoire et utilisation de la ressource
            </h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 leading-relaxed">
              Si les présentes conditions d'utilisation n'ont pas un caractère obligatoire, elles demeurent 
              nécessaires pour informer les utilisateurs des droits concédés. Dans le cadre du service 
              éducatif proposé par <strong className="text-gray-900">TANOED</strong>, l'usage est 
              strictement limité à :
            </p>
            
            <div className="space-y-3 ml-4">
              {[
                "La gestion pédagogique des résultats des élèves aux évaluations",
                "Le suivi pédagogique et l'analyse de la progression des élèves dans le cadre strictement scolaire",
                "La visualisation des données pédagogiques réservées aux enseignants et personnels éducatifs du second degré"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl mt-6">
              <p className="text-gray-700">
                Les usages collectifs (captures d'écran, démonstrations, illustrations) sont exclusivement 
                autorisés à des fins pédagogiques internes à l'établissement scolaire.
              </p>
            </div>
          </div>
        </section>

        {/* Collecte et stockage */}
        <section className="bg-white  flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Database className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Données collectées
            </h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 font-medium mb-3">
              <strong className="text-gray-900">TANOED collecte et stocke</strong> :
            </p>
            
            <div className="grid gap-3">
              {[
                { icon: "👤", text: "Nom, prénom, identifiant GAR des utilisateurs" },
                { icon: "🏫", text: "Établissement scolaire et classe" },
                { icon: "📊", text: "Résultats aux évaluations et analyses pédagogiques associées" }
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-xl flex items-center gap-3">
                  <span className="text-2xl">{item.icon}</span>
                  <p className="text-gray-700">{item.text}</p>
                </div>
              ))}
            </div>
             <div className="bg-gray-50 p-6 rounded-xl">
                <p className="text-sm text-gray-700 mb-3">Plus particulièrement, le GAR transmet à SAS TANOED les données suivantes :</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700 ">
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[UAI] Code établissement</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[IDO] Identifiant opaque</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[idENT] Code projet de l'ENT</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[PRO] Profil</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[DIV] Division(s)</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[GRO] Groupe(s)</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[NOM] Nom d'usage</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[PRE] Prénom usuel</span>
                  <span className="font-mono bg-white px-3 py-1.5 rounded border border-gray-200">[P_MAT] Données enseignant</span>
                </div>
              </div>
            <div className="bg-green-50 p-4 rounded-xl flex items-center gap-3 mt-4">
              <Shield className="w-5 h-5 text-green-600" />
              <p className="text-gray-700">
                Ces données sont utilisées exclusivement pour la fourniture des services précités 
                et ne sont en aucun cas communiquées à des tiers.
              </p>
            </div>
          </div>
        </section>

        {/* Collecte des données personnelles */}
        <section className="bg-white flex flex-col  rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Users className="w-6 h-6 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Collecte des données personnelles
            </h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed">
            Les données transmises sont traitées dans le respect de la finalité de traitement 
            et la durée de conservation préconisées par le GAR. Se référer aux mentions 
            d'information RGPD de la ressource pour plus de précisions.
          </p>
          
        </section>

        {/* Obligations de l'utilisateur */}
        <section className="bg-white flex flex-col  rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-orange-100 rounded-xl">
              <UserCheck className="w-6 h-6 text-orange-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Obligations de l'utilisateur
            </h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 mb-3">L'utilisateur s'engage à :</p>
            
            <div className="space-y-3">
              {[
                "Utiliser le service exclusivement dans le cadre pédagogique précisé",
                "Ne pas tenter d'accéder à des données auxquelles il n'est pas autorisé",
                "Signaler à TANOED tout dysfonctionnement ou problème de sécurité rencontré lors de l'utilisation du service"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-orange-50 p-4 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Responsabilités */}
        <section className="bg-white flex flex-col  rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Responsabilités
            </h2>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 leading-relaxed">
              TANOED s'engage à maintenir le service en état de fonctionnement optimal, 
              sans toutefois pouvoir garantir l'absence totale d'incidents techniques ou 
              interruptions momentanées.
            </p>
            
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
              <p className="text-gray-700">
                TANOED ne saurait être tenu responsable en cas d'utilisation non conforme 
                du service par l'utilisateur ou en cas de force majeure (incidents techniques 
                indépendants de sa volonté, coupure de connexion internet, etc.).
              </p>
            </div>
          </div>
        </section>

        {/* Politique de mise à jour */}
        <section className="bg-white  flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-cyan-100 rounded-xl">
              <RefreshCw className="w-6 h-6 text-cyan-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Politique de mise à jour
            </h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed">
            TANOED se réserve le droit de faire évoluer son service dans le respect des 
            contraintes de conformité fixées par le GAR. Ces évolutions peuvent intervenir 
            à tout moment, sans préavis, dans le but d'améliorer le service fourni.
          </p>
        </section>

        {/* Suspension et résiliation */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gray-100 rounded-xl">
              <Ban className="w-6 h-6 text-gray-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Suspension et résiliation
            </h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed">
            En cas de non-respect avéré des présentes conditions, TANOED se réserve le droit 
            de suspendre temporairement ou définitivement l'accès au service via le GAR, 
            conformément aux dispositions prévues par le contrat GAR.
          </p>
        </section>

        {/* Cession du contrat */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-pink-100 rounded-xl">
              <FileSignature className="w-6 h-6 text-pink-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Cession du contrat
            </h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed">
            Les présentes CGU ne peuvent faire l'objet d'aucune cession ou transmission 
            par l'utilisateur, sans l'accord préalable et écrit de TANOED.
          </p>
        </section>

        {/* Modification des CGU */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-yellow-100 rounded-xl">
              <Edit3 className="w-6 h-6 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Modification des Conditions Générales d'Utilisation
            </h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed">
            TANOED se réserve le droit de modifier à tout moment les présentes CGU, 
            en conformité avec les exigences du GAR. Toute modification fera l'objet 
            d'une mention particulière sur le site ou via le GAR.
          </p>
        </section>

        {/* Droit applicable */}
        <section className="bg-white flex flex-col rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <Scale className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Droit applicable
            </h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed">
            Les présentes conditions générales d'utilisation sont régies par le droit français. 
            Tout litige relatif à leurs interprétations ou à leur exécution relève de la compétence 
            exclusive des juridictions du ressort du siège social de TANOED.
          </p>
        </section>

        {/* Pied de page */}
        <div className="text-center mt-12">
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Calendar className="w-4 h-4" />
            <p className="text-sm">
              <strong>Dernière mise à jour :</strong> Juillet 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CGU;